import { refreshTokenThunk } from '~/store/users/thunks.ts';
import type { AppDispatch } from '~/store/store.ts';
import {
  IS_AUTHENTICATED_KEY,
  MANAGER_PATH_PARTS,
  ROLE_ROUTES,
  SUPERVISOR_INFO_ROUTE_ACCESS,
} from '~/modules/auth/constants';
import { ManagerRole } from '~/constants/clientTypes';
import { TCovRights, TUserRole } from '~/store/users/types';
import { jwtDecode } from 'jwt-decode';

let refreshPromise: Promise<string | null> | null = null;

export const refreshAccessToken = async (dispatch: AppDispatch) => {
  refreshPromise;
  if (refreshPromise) {
    const token = await refreshPromise;
    refreshPromise = null;
    return token;
  }
  try {
    const isAuthenticated = localStorage.getItem(IS_AUTHENTICATED_KEY);
    if (!isAuthenticated) return;
    refreshPromise = dispatch(refreshTokenThunk()).unwrap();
    const token = await refreshPromise;
    refreshPromise = null;
    return token;
  } catch (error) {
    console.error(error);
    return;
  }
};

const normalizePathname = (value?: string | null) => {
  if (!value) {
    return '';
  }

  return value.split('?')[0]?.split('#')[0] ?? '';
};

const matchesPattern = (pattern: string, path: string): boolean => {
  const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$');
  return regex.test(path);
};

export const isManagerRoutePath = (value?: string | null) => {
  const pathname = normalizePathname(value);
  return MANAGER_PATH_PARTS.some((part) => pathname?.startsWith(part));
};

export const resolveSignInPathByRoute = (pathname?: string | null) =>
  isManagerRoutePath(pathname) ? '/manager/sign-in' : '/sign-in';

const isInAnyRoleList = (path: string): boolean => {
  return Object.values(ROLE_ROUTES).some((patterns) =>
    patterns.some((pattern) => matchesPattern(pattern, path))
  );
};

const getSupervisorInfoRouteAccessFlag = (path: string) =>
  Object.entries(SUPERVISOR_INFO_ROUTE_ACCESS).find(([pattern]) =>
    matchesPattern(pattern, path)
  )?.[1];

export const isSupervisorControlledCovenantsRoute = (path: string): boolean =>
  !!getSupervisorInfoRouteAccessFlag(path);

export const hasSupervisorInfoRouteAccess = (
  path: string,
  covenantsRights?: TCovRights | null
): boolean => {
  const accessFlag = getSupervisorInfoRouteAccessFlag(path) || '';
  return !!covenantsRights?.[accessFlag as keyof typeof covenantsRights];
};

export const isRouteRestricted = (
  path: string,
  token: string | null,
  userRoles?: TUserRole,
  covenantsRights?: TCovRights | null
): boolean => {
  if (isSupervisorControlledCovenantsRoute(path) && !isInAnyRoleList(path)) {
    return !token || !hasSupervisorInfoRouteAccess(path, covenantsRights);
  }

  if (isInAnyRoleList(path)) {
    const allowedRoles = (Object.keys(ROLE_ROUTES) as ManagerRole[]).filter((role) =>
      ROLE_ROUTES[role].some((pattern) => matchesPattern(pattern, path))
    );

    return (
      !allowedRoles.some((role) => userRoles?.includes(role)) &&
      !hasSupervisorInfoRouteAccess(path, covenantsRights)
    );
  }

  return !token;
};

export const getTokenExpiry = (token: string) => {
  if (!token) return;

  const payload = jwtDecode(token);
  if (!payload || typeof payload.exp !== 'number') return null;
  return payload.exp * 1000;
};
